// DiscoverFlixRow.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';

import FlixCard from '../container/FlixCard.js';
import { InfiniteScroller, ScrollItem } from './InfiniteScroller.jsx';
import WithIntersectionObserver from './WithIntersectionObserver.jsx';
import HydrationBoundary from '../component/HydrationBoundary.jsx';
import { getIosVersion } from '../resource/getUserAgent.js';
import { freezoneFeed, freezoneType } from '../resource/freezoneConstants.js';

import MoreIconSrc from '../../img/ic_arrow_right_white.svg';
import { color } from '../style/variables.js';
import { flixRowAutoPlayWidth } from '../style/variables.js';

const isServer = typeof window === 'undefined';

export class DiscoverFlixRow extends React.PureComponent {
  state = {
    shouldAutoPlay: false,
    isFirstFrame: true,
  };
  nextTicks = [];
  messagesElement = undefined;
  setShouldAutoPlay = debounce(() => {
    this.setState({
      shouldAutoPlay: window.screen.width < flixRowAutoPlayWidth,
    });
  }, 500);
  handleMessagesScroll = debounce(() => {
    const { category, setScrollLeft } = this.props;
    if (this.messagesElement) {
      setScrollLeft({
        scrollLeft: this.messagesElement.scrollLeft,
        category,
      });
    }
  }, 500);
  componentDidMount() {
    const { isFetched } = this.props;
    if (window.__IS_HYDRATED__ || !isFetched) {
      // don't need to hydrate since SSR data is empty.
      // mostly for local dev env.
      window.__IS_HYDRATED__ = 'DiscoverFlixRow';
    }
    this.nextTicks.push(
      setTimeout(() => this.setState({ isFirstFrame: false }))
    );
    this.setShouldAutoPlay();
    window.addEventListener('resize', this.setShouldAutoPlay);
  }
  componentDidUpdate(prevProps) {
    const { isAuthed, shouldRefresh, category, fetchFeeds, scrollLeft } =
      this.props;
    if (isAuthed !== prevProps.isAuthed) {
      this.nextTicks.push(
        setTimeout(() => fetchFeeds({ page: 1, type: category }))
      );
    }
    if (shouldRefresh && shouldRefresh !== prevProps.shouldRefresh) {
      fetchFeeds({
        page: 1,
        type: category,
        shouldSkipLocalCache: shouldRefresh,
      });
    }
    if (this.messagesElement && scrollLeft > this.messagesElement.scrollLeft) {
      this.messagesElement.scrollLeft = scrollLeft;
    }
  }
  componentWillUnmount() {
    clearTimeout(this.intersectionTimer);
    this.setShouldAutoPlay.cancel();
    window.removeEventListener('resize', this.setShouldAutoPlay);
    this.nextTicks.forEach(clearTimeout);
    if (!window.__IS_HYDRATED__) {
      window.__IS_HYDRATED__ = 'DiscoverFlixRow';
    }
  }

  handleIntersection = ({ isIntersecting }, unobserve) => {
    const { isFetched, fetchFeeds, category } = this.props;
    clearTimeout(this.intersectionTimer);
    if (isIntersecting) {
      this.intersectionTimer = setTimeout(() => {
        unobserve();
        if (!isFetched) {
          fetchFeeds({ page: 1, type: category });
        }
      }, 100);
    }
  };

  renderHydrateFlixCard = ({
    messageId,
    category,
    index,
    shouldPlay,
    isIntersecting,
  }) => {
    return (
      <HydrationBoundary
        shouldHydrate={isIntersecting}
        wrapper={<FlixCardWrapper onClick={this.handleCardClick} />}
      >
        <FlixCard
          messageId={messageId}
          shouldPlay={shouldPlay}
          categoryId={category}
          categoryIndex={index}
          shouldTrackEvent
          shouldShowTooltip
          shouldShowAvatar
          shouldUseLazyImage={false}
          shouldAnimateLoader={isIntersecting}
          isIntersecting={isIntersecting}
        />
      </HydrationBoundary>
    );
  };

  renderFlixCard = ({
    messageId,
    category,
    index,
    shouldPlay,
    isIntersecting,
    isFirstFrame,
  }) => {
    const shouldRenderData = isIntersecting && !isFirstFrame;
    return (
      <FlixCardWrapper onClick={this.handleCardClick}>
        <FlixCard
          messageId={shouldRenderData ? messageId : null}
          shouldPlay={shouldPlay}
          categoryId={category}
          categoryIndex={index}
          shouldTrackEvent
          shouldShowTooltip
          shouldShowAvatar
          shouldAnimateLoader={shouldRenderData}
          isIntersecting={isIntersecting}
        />
      </FlixCardWrapper>
    );
  };

  renderPlaceholders = () => {
    return (
      <MessagesWrapper>
        <Messages>
          {Array(10)
            .fill()
            .map((elem, index) => (
              <FlixCardWrapper key={index}>
                <FlixCard messageId={null} shouldShowAvatar />
              </FlixCardWrapper>
            ))}
        </Messages>
      </MessagesWrapper>
    );
  };

  renderListContent = () => {
    const { isFirstFrame, shouldAutoPlay } = this.state;
    const {
      messageIds,
      nextPage,
      isFetching,
      fetchFeeds,
      category,
      shouldPlayFeedId,
    } = this.props;
    const shouldHydrate = isServer || !window.__IS_HYDRATED__;
    const renderFlixCard = shouldHydrate
      ? this.renderHydrateFlixCard
      : this.renderFlixCard;
    return (
      <MessageScroller
        onScroll={this.handleMessagesScroll}
        ref={el => (this.messagesElement = el)}
      >
        <WithIntersectionObserver
          shouldKeepObserve={true}
          onChange={this.handleIntersection}
        >
          {() => (
            <InfiniteScroller
              axis="x"
              useWindow={false}
              pageStart={1}
              hasMore={nextPage !== null}
              loadMore={() => fetchFeeds({ page: nextPage, type: category })}
              isFetching={isFetching}
              fetchNextPage={
                nextPage
                  ? () => fetchFeeds({ page: nextPage, type: category })
                  : null
              }
              threshold={100}
            >
              <MessagesWrapper>
                <Messages>
                  <ScrollItem
                    loader={
                      <FlixCardWrapper>
                        <FlixCard shouldShowAvatar />
                      </FlixCardWrapper>
                    }
                  >
                    {messageIds.length
                      ? messageIds.map((messageId, index) => (
                          <WithIntersectionObserver
                            key={messageId}
                            shouldKeepObserve
                            threshold={1}
                          >
                            {({ intersectionRatio }) => {
                              return (
                                <WithIntersectionObserver threshold={0}>
                                  {({ isIntersecting }) => {
                                    const shouldPlay =
                                      shouldAutoPlay &&
                                      intersectionRatio >= 1 &&
                                      shouldPlayFeedId === category;
                                    return renderFlixCard({
                                      messageId,
                                      category,
                                      index,
                                      shouldPlay,
                                      isIntersecting,
                                      isFirstFrame,
                                    });
                                  }}
                                </WithIntersectionObserver>
                              );
                            }}
                          </WithIntersectionObserver>
                        ))
                      : Array(10)
                          .fill()
                          .map((elem, index) => (
                            <FlixCardWrapper key={index}>
                              <FlixCard messageId={null} shouldShowAvatar />
                            </FlixCardWrapper>
                          ))}
                  </ScrollItem>
                </Messages>
              </MessagesWrapper>
            </InfiniteScroller>
          )}
        </WithIntersectionObserver>
      </MessageScroller>
    );
  };
  render() {
    const { t, isFetched, category, messageIds } = this.props;
    if (messageIds.length === 0 && isFetched) {
      return null;
    }
    return (
      <StyledDiscoverFlixRow>
        <Header>
          <HeaderLeft>
            <Title>{t(category)}</Title>
          </HeaderLeft>
          <HeaderRight>
            <CategoryMoreButton
              to={
                category === freezoneFeed.AMATEUR
                  ? `/freezone/${freezoneType.AMATEUR}`
                  : `/flix-categories/${category}`
              }
              title={`SWAG ${t(category)}`}
            >
              <MoreButtonText>{t('more')}</MoreButtonText>
              <MoreButtonIcon src={MoreIconSrc} alt={t('more')} />
            </CategoryMoreButton>
          </HeaderRight>
        </Header>
        {this.renderListContent()}
      </StyledDiscoverFlixRow>
    );
  }
}

DiscoverFlixRow.propTypes = {
  t: PropTypes.func.isRequired,
  category: PropTypes.string,
  shouldPlayFeedId: PropTypes.string,
  fetchFeeds: PropTypes.func,
  isAuthed: PropTypes.bool,
  isFetched: PropTypes.bool,
  isFetching: PropTypes.bool,
  shouldRefresh: PropTypes.bool,
  messageIds: PropTypes.array,
  nextPage: PropTypes.number,
  setScrollLeft: PropTypes.func,
  scrollLeft: PropTypes.number,
};

DiscoverFlixRow.defaultProps = {
  isAuthed: false,
  isFetching: false,
  shouldRefresh: false,
  isFetched: false,
  category: '',
  shouldPlayFeedId: '',
  fetchFeeds: () => null,
  messageIds: [],
  nextPage: null,
  setScrollLeft: () => null,
  scrollLeft: 0,
};

const StyledDiscoverFlixRow = styled.article`
  background-color: ${color.black};
  min-height: 320px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  display: flex;
  padding: 16px 16px 0px;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
`;

const HeaderRight = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  min-width: max-content;
  margin-left: 8px;
`;

const CategoryMoreButton = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${color.white};
  padding: 4px 8px;
  border-radius: 4px;
  border: solid 1px ${color.white};
  white-space: nowrap;
  :hover {
    opacity: 0.7;
  }
`;

const MoreButtonText = styled.span`
  margin-right: 4px;
  font-size: 12px;
  line-height: 1;
`;

const MoreButtonIcon = styled.img`
  height: 16px;
  width: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  flex: 1;
  display: flex;
  align-items: center;
  text-align: justify;
`;

// On iOS 13, scrolling momentum is applied to elements with scrollbar automatically,
// and "-webkit-overflow-scrolling: touch;" causes rendering bug, so the attribute is not necessary.
// On iOS 12 and below, "-webkit-overflow-scrolling: touch;" is necessary for momentum scrolling.
const MessageScroller = styled.div`
  flex: 1;
  width: 100vw;
  overflow-x: auto;
  overflow-y: hidden;
  ${getIosVersion().major < 13 ? '-webkit-overflow-scrolling: touch;' : ''}

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const MessagesWrapper = styled.div`
  display: inline-block;
`;

const Messages = styled.div`
  padding: 12px 16px 0px;
  display: flex;
`;

const FlixCardWrapper = styled.div`
  flex: none;
  margin-right: 8px;
  border-radius: 4px;
  width: 260px;
  :last-child {
    margin-right: 0;
  }
`;

export default withTranslation()(DiscoverFlixRow);
